'use client'

import ProjectNavbar from '@renderer/components/ProjectNavbar'
import Spinner from '@renderer/components/Spinner'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { FaEllipsis, FaCoins } from 'react-icons/fa6'
import { FaSort, FaRedo } from 'react-icons/fa'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useTranslation } from 'react-i18next'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useAuthStore } from '@renderer/stores/authStore'
import { capabilities, hasElectronPreload } from '@renderer/lib/runtime'

interface Project {
  _id: string
  title: string
  thumbnail: string
  updatedAt: Date
  error?: string
}

interface UserContext {
  user: {
    username: string
  }
}

const ProjectsPage: React.FC = () => {
  console.log('🔍 ProjectsPage component loaded')
  const { t } = useTranslation()
  const { user } = useOutletContext<UserContext>()
  const [projects, setProjects] = useState<Project[]>([])
  const [showMenu, setShowMenu] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [fadeIn, setFadeIn] = useState<boolean>(false)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set())
  const [completelyFailedThumbnails, setCompletelyFailedThumbnails] = useState<Set<string>>(new Set())
  const menuRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const { autoStart, defaultProjectId } = useSettingsStore()
  const { credits, fetchSubscriptionAndCredits } = useCreditsStore()
  const initializeCookie = useAuthStore((state) => state.initializeCookie)
  const logout = useAuthStore((state) => state.logout)
  const isKlakKlikKluk = window.location.hostname.includes('klakklikkluk')

  const fetchProjects = useCallback(async (retry = false): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      // On retry, refresh auth tokens/cookies first to avoid 401 errors
      if (retry && hasElectronPreload()) {
        try {
          await initializeCookie()
          // Small delay to ensure cookies are set
          await new Promise((resolve) => setTimeout(resolve, 100))
        } catch (err) {
          console.warn('[PROJECTS] Failed to refresh auth on retry:', err)
          // Continue anyway - might still work
        }
      }

      const response = await fetch(`${process.env.VITE_API_URL}/api/projects`, {
        credentials: 'include'
      })

      if (!response.ok) {
        if (response.status === 401 && retry) {
          setLoading(false)
          await logout()
          navigate('/login', { state: { message: 'Session expired' }, replace: true })
          return
        }
        throw new Error(`Failed to load projects: ${response.status} ${response.statusText}`)
      }

      const data: Project[] = await response.json()
      setProjects(data)
      setLoading(false)
      setFadeIn(true)

      if (autoStart && defaultProjectId) {
        const projectExists = data.some((project) => project._id === defaultProjectId)
        if (projectExists) {
          navigate(`/${defaultProjectId}`)
        }
      }
    } catch (err) {
      console.error('Error fetching projects:', err)
      setError(err instanceof Error ? err.message : 'Failed to load projects')
      setLoading(false)
      setFadeIn(false)
    }
  }, [autoStart, defaultProjectId, navigate, initializeCookie, logout])

  useEffect(() => {
    fetchProjects()
    console.log('🟡 Calling fetchSubscriptionAndCredits from Projects page')
    fetchSubscriptionAndCredits().catch((err) => {
      console.error('Error fetching subscription and credits:', err)
      // Don't show error for credits, just log it
    })
  }, [fetchProjects, fetchSubscriptionAndCredits])

  // Log user session data when credits or user changes
  useEffect(() => {
    if (user?.username && credits !== null && credits !== undefined) {
      console.log('=== User Session Data ===')
      console.log('Username:', user.username)
      console.log('Current Credits:', credits)
      console.log('Timestamp:', new Date().toISOString())
      console.log('========================')
    }
  }, [credits, user])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleMenuToggle = (id: string): void => {
    setShowMenu((prev) => (prev === id ? null : id))
  }

  const toggleSortOrder = (): void => {
    setSortOrder((current) => (current === 'newest' ? 'oldest' : 'newest'))
  }

  const handleThumbnailError = (projectId: string, thumbnailUrl: string): void => {
    // If this thumbnail failed with crossOrigin="anonymous", retry without it
    if (!failedThumbnails.has(projectId)) {
      console.warn(`[PROJECTS] Thumbnail failed with CORS for project ${projectId}, retrying without crossOrigin`)
      setFailedThumbnails((prev) => new Set(prev).add(projectId))
      // Force re-render by updating the src with a cache-busting parameter
      // The component will re-render and try again without crossOrigin
    } else {
      // Already tried without crossOrigin, mark as completely failed
      console.error(`[PROJECTS] Thumbnail completely failed to load for project ${projectId}: ${thumbnailUrl}`)
      setCompletelyFailedThumbnails((prev) => new Set(prev).add(projectId))
    }
  }

  const handleThumbnailLoad = (projectId: string): void => {
    // If image loaded successfully, remove from completely failed set
    // Don't remove from failedThumbnails - if it loaded without CORS, keep it that way
    if (completelyFailedThumbnails.has(projectId)) {
      setCompletelyFailedThumbnails((prev) => {
        const newSet = new Set(prev)
        newSet.delete(projectId)
        return newSet
      })
    }
  }

  const getFilteredAndSortedProjects = (): Project[] => {
    let filtered = projects

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter((project) =>
        project.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime()
      const dateB = new Date(b.updatedAt).getTime()

      if (sortOrder === 'newest') {
        return dateB - dateA // Newest first
      } else {
        return dateA - dateB // Oldest first
      }
    })

    return sorted
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <ProjectNavbar username={user.username} credits={credits} />
      <div className="w-full p-4 sm:p-8 lg:p-12">
        <div className="w-full flex flex-col gap-4 mb-8">
          <div className="flex justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{t('projects.title', 'Projects')}</h1>
              <p>{t('projects.subtitle', 'Manage your projects here')}</p>
            </div>
            {!isKlakKlikKluk && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg shadow-sm">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t('projects.sessionCredits', 'Session Credits')}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <span className="text-xl font-bold text-blue-600">{credits}</span>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            console.log('=== User Session Data ===')
                            console.log('Username:', user.username)
                            console.log('Current Credits:', credits)
                            console.log('Timestamp:', new Date().toISOString())
                            console.log('========================')
                            fetchSubscriptionAndCredits()
                          } catch (error) {
                            console.error('Error refreshing credits:', error)
                          }
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title={t('projects.refreshCredits', 'Refresh credits')}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="h-8 w-px bg-gray-200"></div>
                  <a
                    href={`${process.env.VITE_API_URL}/dashboard/projects?modal=topup`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <FaCoins className="text-sm" />
                    <span>{t('projects.topUpButton', 'Top Up')}</span>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Search and Sort Controls */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-full sm:w-auto sm:max-w-md">
              <input
                type="text"
                placeholder={t('projects.searchPlaceholder', 'Search by title...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={toggleSortOrder}
              className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors border-b-2 border-transparent hover:border-blue-600 pb-1"
              title={
                sortOrder === 'newest'
                  ? t('projects.sortNewest', 'Sorted: Newest first')
                  : t('projects.sortOldest', 'Sorted: Oldest first')
              }
            >
              <FaSort />
              <span className="font-medium">
                {sortOrder === 'newest'
                  ? t('projects.newest', 'Newest')
                  : t('projects.oldest', 'Oldest')}
              </span>
            </button>
          </div>
        </div>
        {loading ? (
          <div className="flex flex-col justify-center items-center py-16">
            <div className="relative">
              <Spinner />
            </div>
            <p className="text-lg text-gray-600 mt-4 font-medium">
              {t('projects.loading', 'Loading projects...')}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {t('projects.loadingSubtext', 'Please wait')}
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="max-w-md w-full space-y-4">
              <div className="text-center space-y-2">
                <p className="text-xl font-semibold text-red-600">
                  {t('projects.errorTitle', 'Failed to load projects')}
                </p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => fetchProjects(true)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm"
                >
                  <FaRedo />
                  <span>{t('projects.retry', 'Retry')}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`projects-list grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'
              }`}
          >
            {getFilteredAndSortedProjects().length === 0 ? (
              <div className="placeholder">
                {searchQuery.trim()
                  ? t('projects.noProjectsFound', 'No projects found matching your search')
                  : t('projects.noProjects', 'No projects available')}
              </div>
            ) : (
              getFilteredAndSortedProjects().map((project) => (
                <div
                  key={project._id}
                  className={`relative pb-4 pt-2 px-2 rounded-lg shadow-lg transition-opacity duration-500 bg-white ${selectedProjectId === project._id ? 'border border-blue-500' : ''
                    }`}
                  onClick={() => {
                    if (capabilities.isMobile) {
                      navigate(`/${project._id}`)
                    } else {
                      setSelectedProjectId(project._id)
                    }
                  }}
                  onDoubleClick={() => !capabilities.isMobile && navigate(`/${project._id}`)}
                >
                  {completelyFailedThumbnails.has(project._id) ? (
                    <div className="w-full h-40 bg-gray-200 rounded-lg mb-2 flex items-center justify-center cursor-pointer">
                      <div className="text-center text-gray-400">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 mx-auto mb-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-xs">Image unavailable</p>
                      </div>
                    </div>
                  ) : (
                    <img
                      key={`${project._id}-${failedThumbnails.has(project._id) ? 'no-cors' : 'cors'}`}
                      src={project.thumbnail}
                      alt={project.title}
                      crossOrigin={failedThumbnails.has(project._id) ? undefined : 'anonymous'}
                      width={300}
                      height={200}
                      className="w-full h-40 object-cover rounded-lg mb-2 cursor-pointer"
                      onError={() => handleThumbnailError(project._id, project.thumbnail)}
                      onLoad={() => handleThumbnailLoad(project._id)}
                    />
                  )}
                  <h2 className="text-lg font-semibold ml-2">{project.title}</h2>
                  <p className="text-sm text-gray-500 ml-2">
                    {t('projects.lastUpdated', 'Last Updated')}:{' '}
                    {new Date(project.updatedAt).toLocaleDateString()}{' '}
                    {new Date(project.updatedAt).toLocaleTimeString()}
                  </p>
                  <button
                    title={t('projects.menuOptions', 'Options')}
                    type="button"
                    onClick={() => handleMenuToggle(project._id)}
                    className="absolute top-6 right-6 text-lg"
                  >
                    <FaEllipsis />
                  </button>
                  {showMenu === project._id && (
                    <div
                      ref={menuRef}
                      className="absolute top-8 right-12 bg-white border rounded shadow-lg text-xs"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/${project._id}`)}
                        className="block px-4 w-full py-2 hover:bg-gray-200"
                      >
                        {t('projects.run', 'Run')}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectsPage
